import RequestAbstract from "../abstract";
import { fileNameFromDisposition, saveBlob } from "../../utils/fileDownload";

const EXPORT_FALLBACK_FILE_NAME = 'wallets.csv';

export default class PublicWallets extends RequestAbstract {
    data;
    constructor(data) {
        super(data);
        this.data = data;
        this.link = "api/public-wallets";
    }

    /**
     * The requisites templates a gateway can name, each with its fields
     * (`[{ template, fields: [{ key, required, format, options? }] }]`): what the
     * wallet editor builds its form from and what the cabinet checks a wallet of
     * such a gateway against.
     */
    async requisitesTemplates() {
        return await this.loadWithAuth(`${this.link}/requisites-templates`, { method: 'GET' });
    }

    /**
     * Put a wallet back into the pool core picks from, or take it out. The
     * `wallet-toggle` action, not update; a repeat changes nothing. Resolves to
     * the cabinet envelope with the wallet as `data`.
     */
    async enable(id) {
        return await this.post(`${this.link}/${id}/enable`, {});
    }

    async disable(id) {
        return await this.post(`${this.link}/${id}/disable`, {});
    }

    /**
     * Export the wallets matching a ListRequest as CSV and hand the file to the
     * browser. Resolves to the usual envelope shape: `error: 'OK'` with
     * `data.fileName` once the download has been started, or the server's
     * error envelope (`{error: 'API_ERROR', message}`) when it refused.
     *
     * Not loadWithAuth/loadBinary: the file name lives in Content-Disposition,
     * which loadBinary does not return, and loadBinary flattens a JSON error
     * envelope into an "HTTP error!" string.
     */
    async exportCsv(body) {
        const { language } = this._config;
        let resp = { code: 'initial', status: 0 };

        try {
            const response = await fetch(this.getFullLink(`${this.link}/csv/export`), {
                method: 'POST',
                headers: {
                    'X-Auth-Token': localStorage.token,
                    'Language': localStorage.language || language,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(body)
            });
            resp.status = response.status;

            const contentType = response.headers.get('Content-Type') || '';
            if (!response.ok || contentType.includes('application/json')) {
                // The cabinet answers a refusal (422 "Too many wallets…") with
                // its JSON envelope rather than a file.
                let envelope = {};
                try {
                    envelope = JSON.parse(await response.text());
                } catch (e) {
                    // Not JSON — a proxy's error page, say. Its body is no use
                    // to anyone reading a snackbar; the status is.
                }
                resp = {
                    ...resp,
                    ...envelope,
                    error: envelope.error && envelope.error !== 'OK' ? envelope.error : 'EXPORT_FAILED',
                    message: envelope.message || `HTTP error! status: ${response.status}`
                };
            } else {
                const blob = await response.blob();
                const fileName =
                    fileNameFromDisposition(response.headers.get('Content-Disposition')) ||
                    EXPORT_FALLBACK_FILE_NAME;
                saveBlob(blob, fileName);
                resp = { ...resp, error: 'OK', data: { fileName, size: blob.size } };
            }
        } catch (error) {
            resp = { ...resp, error: 'EXPORT_FAILED', message: error.message };
        }

        this.handleStatus(resp);
        return resp;
    }

    /**
     * Import wallets from a CSV. `formData` carries `file`, `gatewayId`, and
     * optionally `merchantTerminalId` and `dryRun` ("true" validates only).
     * Resolves to the cabinet envelope; `data` is the import result.
     *
     * fileUpload drops the JSON Content-Type so the browser sets the multipart
     * one, boundary included.
     */
    async importCsv(formData) {
        return await this.loadWithAuth(
            `${this.link}/csv/import`,
            {
                method: 'POST',
                body: formData
            },
            { fileUpload: true }
        );
    }
}
